import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import HelpPanel from './HelpPanel';
import { closeHelpPanel, useHelpPanel } from './helpPanelStore';
import TooltipLayer from './TooltipLayer';
import TourOverlay from './TourOverlay';
import {
  isActiveHost,
  registerHost,
  startTour,
  useTourState,
} from './tourStore';

// Mount once on each Forms page (list, builder, submissions). It brings the
// tooltip layer, the Help & tours panel and the product tour, and tells the
// tour how to move this page around:
//
//   navigate(where), where is one of
//     'list'           go to the Forms list
//     'builder'        open a form in the builder (the current form, or the
//                      first one when on the list)
//     'submissions'    open a form's Submissions (pick the first form with
//                      entries when the current one has none)
//     'entry'          open the first entry's card (Submissions page)
//     'export'         open the Export dialog (Submissions page)
//     'close-overlays' close the entry card, Columns/Export dialogs and any
//                      other modal or drawer the tour may have opened
//     'select-field'   (builder) select a field so its settings show
//     'form-settings'  (builder) show the form's settings
//   A page ignores requests it can't act on; the steps that needed them are
//   skipped.
//   It may return a promise. Page changes are awaited: the tour waits for
//   the FormsHelp of the requested `page` to mount before going on.
//
// The tour never starts on its own: the user picks it from Help & tours.
// (The builder also mounts inside W-9 Settings, where an unasked-for tour
// would walk the page off to Forms.)

let hostCount = 0;

const FormsHelp = ({ page, navigate }) => {
  const idRef = useRef(null);
  if (idRef.current == null) {
    hostCount += 1;
    idRef.current = `forms-help-${hostCount}`;
  }
  const hostId = idRef.current;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const panel = useHelpPanel();
  useTourState();

  useEffect(() => {
    const unregister = registerHost({
      id: hostId,
      page,
      navigate: (where) => navigateRef.current(where),
    });
    return () => {
      unregister();
      closeHelpPanel();
    };
  }, [hostId, page]);

  const start = (chapterId) => {
    closeHelpPanel();
    startTour(chapterId);
  };

  return (
    <>
      <TooltipLayer />
      {panel.open && isActiveHost(hostId) ? (
        <HelpPanel
          anchor={panel.anchor}
          onClose={closeHelpPanel}
          onStart={start}
        />
      ) : null}
      <TourOverlay hostId={hostId} />
    </>
  );
};

FormsHelp.propTypes = {
  page: PropTypes.oneOf(['list', 'builder', 'submissions']).isRequired,
  navigate: PropTypes.func.isRequired,
};

export default FormsHelp;
